import { useState, useCallback, useEffect } from "react";

/**
 * Custom hook for managing form state driven by a config-based form schema.
 * Handles text inputs, checkboxes (with "Other" text support), and pre-selected values.
 *
 * @param {Array} sections - Array of form section objects, each with a `fields` array.
 * @returns {Object} Form state and handlers
 *
 * @example
 * const { formData, handleInputChange, handleCheckboxChange } = useFormState(sections);
 */
export const useFormState = (sections = []) => {
    const [formData, setFormData] = useState({});
    const [otherTextValues, setOtherTextValues] = useState({});

    // Initialize pre-selected checkboxes from schema
    useEffect(() => {
        const initialFormData = {};
        sections.forEach((section, sectionIndex) => {
            if (!section?.fields) return;
            section.fields.forEach((field, fieldIndex) => {
                if (field.preSelected && field.type === "checkbox" && field.options) {
                    const key = `section_${sectionIndex}_field_${fieldIndex}`;
                    if (!formData[key]) {
                        initialFormData[key] = [...field.options];
                    }
                }
            });
        });
        if (Object.keys(initialFormData).length > 0) {
            setFormData((prev) => ({ ...prev, ...initialFormData }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections]);

    const handleInputChange = useCallback((key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleCheckboxChange = useCallback(
        (sectionIndex, fieldIndex, optionIndex, checked, optionValue) => {
            const key = `section_${sectionIndex}_field_${fieldIndex}`;

            setFormData((prev) => {
                let currentVal = prev[key] || [];

                if (checked) {
                    return { ...prev, [key]: [...currentVal, optionValue] };
                } else {
                    if (optionValue.toLowerCase().includes("other")) {
                        const otherKey = `${key}_otherText`;
                        setOtherTextValues((prevOther) => ({
                            ...prevOther,
                            [otherKey]: "",
                        }));
                    }
                    return {
                        ...prev,
                        [key]: currentVal.filter(
                            (v) => v !== optionValue && !v.startsWith("Other:")
                        ),
                    };
                }
            });
        },
        []
    );

    const handleOtherTextChange = useCallback(
        (sectionIndex, fieldIndex, optionIndex, value) => {
            const key = `section_${sectionIndex}_field_${fieldIndex}`;
            const otherKey = `${key}_otherText`;

            setOtherTextValues((prev) => ({ ...prev, [otherKey]: value }));

            setFormData((prev) => {
                const currentVal = prev[key] || [];
                const filteredVal = currentVal.filter((v) => !v.startsWith("Other:"));
                const newVal = value.trim()
                    ? [...filteredVal, `Other: ${value.trim()}`]
                    : filteredVal;
                return { ...prev, [key]: newVal };
            });
        },
        []
    );

    const resetForm = useCallback(() => {
        setFormData({});
        setOtherTextValues({});
    }, []);

    return {
        formData,
        otherTextValues,
        handleInputChange,
        handleCheckboxChange,
        handleOtherTextChange,
        resetForm,
    };
};
